import styles from './styles.module.scss';
import { Button, FormGroup } from '@starsoft/common/components';
import { CoinType } from '@starsoft/common/models';
import { AnimatePresence } from 'framer-motion';
import { motion } from 'framer-motion';
import { fadeInUp } from './variants';
import { WalletService } from '@starsoft/common/services';
import { TransactionModalsLayoutFooterProps } from './props';
import TransactionModalsWithdrawLayoutFooterSkeleton from './Skeleton';
import { TransactionOptions } from '../../../props';
import {
  CommonModalsKey,
  DepositStep,
  WithdrawStep,
} from '@starsoft/common/enums';
import Fee from '../../../Modals/Withdraw/Content/Form/Layout/Fee';
import FeeLoader from '../../../Modals/Withdraw/Content/Form/Layout/Fee/Loader';
import { useCurrentModal } from '../../../../../hooks/modals/useCurrentModal';
import { useTranslation } from '../../../../../hooks';
import { getMoneyOrAmount } from '@starsoft/common/utils';

/**
 * TransactionModalsLayoutFooter Component
 * This component is responsible for rendering the footer of transaction modals.
 * It handles the display of fees, amounts, and the submit button based on the transaction step and modal type.
 *
 * @template T - A generic type extending TransactionOptions.
 * @param {TransactionModalsLayoutFooterProps<T>} props - The properties for the component.
 * @param {boolean} props.error - Indicates if there is an error.
 * @param {number} props.amount - The transaction amount.
 * @param {string} props.coin - The type of coin used in the transaction.
 * @param {CoinType} props.coinType - The type of coin (Fiat or Crypto).
 * @param {boolean} props.isLoading - Indicates if the data is still loading.
 * @param {object} props.feeOptions - Options related to transaction fees.
 * @param {string} props.i18nNamespace - The namespace for internationalization.
 * @param {boolean} props.isLoadingSubmit - Indicates if the submit action is loading.
 * @param {WithdrawStep | DepositStep} props.transactionStep - The current step in the transaction process.
 * @param {boolean} props.showExternalLink - Indicates if an external link should be shown.
 * @param {boolean} props.isSubmitButtonDisabled - Indicates if the submit button should be disabled.
 *
 * @returns {JSX.Element | null} The rendered component displaying the footer or null if there is an error.
 *
 * @example
 * // Usage example:
 * <TransactionModalsLayoutFooter
 *   error={false}
 *   amount={100}
 *   coin="USD"
 *   coinType={CoinType.Fiat}
 *   isLoading={false}
 *   feeOptions={{ isLoadingFee: false, fee: { receiveAmount: 95 } }}
 *   i18nNamespace="transactions"
 *   isLoadingSubmit={false}
 *   transactionStep={WithdrawStep.Form}
 *   showExternalLink={true}
 *   isSubmitButtonDisabled={false}
 * />
 */
export default function TransactionModalsLayoutFooter<
  T extends TransactionOptions,
>({
  error,
  amount,
  coin,
  coinType,
  isLoading,
  feeOptions,
  i18nNamespace,
  isLoadingSubmit,
  transactionStep,
  showExternalLink,
  isSubmitButtonDisabled,
}: TransactionModalsLayoutFooterProps<T>) {
  const modal = useCurrentModal(CommonModalsKey);
  const { t } = useTranslation('common');
  const { t: tTransaction } = useTranslation(i18nNamespace);

  /**
   * getValue function
   * This function calculates and returns the formatted currency value based on the current modal and transaction details.
   *
   * @returns {string} The formatted currency value.
   */
  function getValue(): string {
    if (modal == CommonModalsKey.Withdraw) {
      return WalletService.maskCurrency({
        amount: getMoneyOrAmount(feeOptions?.fee?.receiveAmount),
        coin,
      });
    }

    return WalletService.maskCurrency({
      amount: getMoneyOrAmount(amount?.toString()),
      coin,
    });
  }

  const hideFooter: boolean =
    [WithdrawStep.Response, DepositStep.Response].includes(transactionStep) ||
    (transactionStep == DepositStep.Form &&
      modal == CommonModalsKey.Deposit &&
      coinType == CoinType.Crypto);

  if (isLoading) {
    return (
      <TransactionModalsWithdrawLayoutFooterSkeleton
        coinType={coinType}
        transactionStep={transactionStep}
      />
    );
  }

  if (error) {
    return null;
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        variants={fadeInUp}
        initial="hidden"
        animate="show"
        exit="hidden"
        className={`${styles.container} ${hideFooter ? styles['container--invisible'] : ''}`}
      >
        {modal != CommonModalsKey.Deposit && (
          <>
            <Fee noChain={coinType == CoinType.Fiat} />
            <div className={styles.container__divider} />
          </>
        )}

        <div className={styles.container__row}>
          <FormGroup
            label={
              modal == CommonModalsKey.Withdraw
                ? tTransaction('sended_amount')
                : t('deposit:deposit_amount')
            }
          >
            <FeeLoader
              isLoading={
                modal == CommonModalsKey.Withdraw
                  ? feeOptions?.isLoadingFee
                  : false
              }
            >
              <p className={styles.container__value}>{getValue()}</p>
            </FeeLoader>
          </FormGroup>

          <Button
            form={modal == CommonModalsKey.Deposit ? 'deposit' : 'withdraw'}
            disabled={isSubmitButtonDisabled}
            isloading={isLoadingSubmit}
            type="submit"
            isSecondary={isSubmitButtonDisabled}
          >
            {t('continue_label')}
            <i
              className={
                showExternalLink
                  ? 'fa-solid fa-arrow-up-right-from-square'
                  : 'fa-solid fa-arrow-right-long'
              }
            />
          </Button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
